import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Heart, FileText, Users as UsersIcon, Plus, Copy, Check, Share2, ChevronRight, Bell, AlertTriangle, UserPlus } from 'lucide-react';
import TopBar from '../components/TopBar';
import BottomNav from '../components/BottomNav';
import AdBanner from '../components/AdBanner';
import Avatar from '../components/Avatar';
import api from '../lib/api';
import { useAuth } from '../contexts/AuthContext';

export default function Family() {
    const navigate = useNavigate();
    const { user } = useAuth();
    const [family, setFamily] = useState(null);
    const [members, setMembers] = useState([]);
    const [requests, setRequests] = useState([]);
    const [copied, setCopied] = useState(false);

    useEffect(() => {
        api.get('/family/me').then((r) => setFamily(r.data.family)).catch(() => {});
        api.get('/family/members').then((r) => setMembers(r.data.members || [])).catch(() => {});
        if (user?.family_role === 'admin') {
            api.get('/family/join-requests').then((r) => setRequests(r.data.requests || [])).catch(() => {});
        }
    }, [user]);

    const copyCode = async () => {
        if (!family?.code) return;
        await navigator.clipboard.writeText(family.code);
        setCopied(true);
        setTimeout(() => setCopied(false), 1500);
    };

    const share = async () => {
        if (!family?.code) return;
        const text = `Únete a mi familia en 4everSync con el código: ${family.code}`;
        if (navigator.share) {
            try { await navigator.share({ title: '4everSync', text }); } catch {}
        } else {
            await navigator.clipboard.writeText(text);
            alert('Mensaje copiado');
        }
    };

    return (
        <div className="min-h-screen bg-surface app-shell pb-28">
            <div className="px-6">
                <TopBar onPremiumClick={() => alert('Próximamente: planes Premium')} onBellClick={() => navigate('/notificaciones')} onMenuClick={() => navigate('/home')} />

                <div className="mt-4 flex items-end justify-between">
                    <div>
                        <p className="text-gray-500 text-sm font-medium">Familia</p>
                        <h1 className="text-3xl font-bold text-brand-navy tracking-tight" data-testid="family-title">Miembros del Hogar</h1>
                    </div>
                </div>

                {family && (
                    <div className="mt-5 bg-brand/5 border border-brand/15 rounded-2xl px-4 py-3 flex items-center justify-between" data-testid="family-code-card">
                        <div>
                            <div className="text-[11px] text-gray-500 font-semibold uppercase tracking-wide">Código de Familia</div>
                            <div className="text-base font-bold text-brand">{family.code}</div>
                        </div>
                        <div className="flex items-center gap-1">
                            <button onClick={copyCode} className="w-10 h-10 rounded-full hover:bg-brand/10 flex items-center justify-center" data-testid="family-copy-code">
                                {copied ? <Check className="w-5 h-5 text-emerald-500" /> : <Copy className="w-5 h-5 text-brand" />}
                            </button>
                            <button onClick={share} className="w-10 h-10 rounded-full hover:bg-brand/10 flex items-center justify-center" data-testid="family-share-code">
                                <Share2 className="w-5 h-5 text-brand" />
                            </button>
                        </div>
                    </div>
                )}

                {user?.family_role === 'admin' && requests.length > 0 && (
                    <button
                        onClick={() => navigate('/familia/solicitudes')}
                        className="mt-3 w-full bg-orange-50 border border-orange-200 rounded-2xl p-3 flex items-center justify-between hover:bg-orange-100 transition"
                        data-testid="family-pending-requests"
                    >
                        <div className="flex items-center gap-3">
                            <div className="w-10 h-10 bg-orange-200 rounded-full flex items-center justify-center">
                                <AlertTriangle className="w-5 h-5 text-orange-700" />
                            </div>
                            <div className="text-left">
                                <div className="text-sm font-bold text-orange-900">{requests.length} solicitud{requests.length > 1 && 'es'} pendiente{requests.length > 1 && 's'}</div>
                                <div className="text-xs text-orange-700">Revísalas y aprueba o rechaza</div>
                            </div>
                        </div>
                        <ChevronRight className="w-5 h-5 text-orange-700" />
                    </button>
                )}

                <div className="mt-5 space-y-3">
                    {members.map((m) => (
                        <MemberCard key={m.id} member={m} onClick={() => navigate(`/familia/miembro/${m.id}`)} />
                    ))}
                </div>
            </div>

            <button
                onClick={() => navigate('/familia/nuevo')}
                className="fixed bottom-[150px] left-[calc(50%-110px)] -translate-x-1/2 px-5 h-12 bg-brand text-white rounded-full font-bold shadow-brand-cta hover:scale-[1.02] active:scale-[0.97] transition flex items-center gap-2 z-30 text-sm"
                data-testid="family-add-member-fab"
            >
                <Plus className="w-4 h-4" /> Nuevo
            </button>
            <button
                onClick={() => navigate('/familia/invitar')}
                className="fixed bottom-[150px] left-[calc(50%+50px)] -translate-x-1/2 px-5 h-12 bg-white border-2 border-brand text-brand rounded-full font-bold shadow-card hover:bg-brand/5 active:scale-[0.97] transition flex items-center gap-2 z-30 text-sm"
                data-testid="family-invite-fab"
            >
                <Bell className="w-4 h-4" /> Invitar
            </button>

            <div className="fixed bottom-[72px] left-1/2 -translate-x-1/2 w-full max-w-[480px] z-20">
                <AdBanner />
            </div>
            <BottomNav />
        </div>
    );
}

function MemberCard({ member, onClick }) {
    const navigate = useNavigate();
    const goTo = (tab) => (e) => {
        e.stopPropagation();
        navigate(`/familia/miembro/${member.id}?tab=${tab}`);
    };
    return (
        <div className="bg-white rounded-2xl shadow-card p-4 cursor-pointer hover:shadow-card-md active:scale-[0.99] transition" onClick={onClick} data-testid={`member-card-${member.id}`}>
            <div className="flex items-center gap-4">
                <Avatar name={member.name} color={member.avatar_color} photo={member.avatar_url} size={56} ring />
                <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                        <h3 className="text-base font-bold text-brand-navy truncate">{member.name || '(Sin nombre)'}</h3>
                        {member.profile_type === 'informative' && <span className="text-[9px] uppercase font-bold px-2 py-0.5 rounded-full bg-gray-100 text-gray-500">info</span>}
                    </div>
                    <p className="text-xs text-gray-500">{member.family_role_label || member.family_role}</p>
                </div>
                <ChevronRight className="w-5 h-5 text-gray-400" />
            </div>
            <div className="mt-3 grid grid-cols-3 gap-2">
                <StatPill icon={Heart} label="Salud" value={member.stats?.health_count || 0} color="text-rose-500 bg-rose-50" onClick={goTo('salud')} testId={`member-${member.id}-stat-salud`} />
                <StatPill icon={FileText} label="Docs" value={member.stats?.docs_count || 0} color="text-blue-500 bg-blue-50" onClick={goTo('documentos')} testId={`member-${member.id}-stat-docs`} />
                <StatPill icon={UsersIcon} label="Contactos" value={member.stats?.contacts_count || 0} color="text-emerald-500 bg-emerald-50" onClick={goTo('contactos')} testId={`member-${member.id}-stat-contactos`} />
            </div>
        </div>
    );
}

function StatPill({ icon: Icon, label, value, color, onClick, testId }) {
    return (
        <button onClick={onClick} className={`flex items-center gap-1.5 px-2 py-1.5 rounded-xl ${color} hover:scale-[1.03] active:scale-[0.97] transition`} data-testid={testId}>
            <Icon className="w-3.5 h-3.5" />
            <span className="text-[11px] font-bold uppercase tracking-wide">{label}</span>
            <span className="text-sm font-bold ml-auto">{value}</span>
        </button>
    );
}
